const SHARE_TOLERANCE = 1e-6;

const FIDELITY_MONTHS = {
  apr: "04",
  aug: "08",
  dec: "12",
  feb: "02",
  jan: "01",
  jul: "07",
  jun: "06",
  mar: "03",
  may: "05",
  nov: "11",
  oct: "10",
  sep: "09",
};

export function parseFidelityExecutions(text, action) {
  const normalizedAction = String(action || "").trim().toUpperCase();
  if (!["BUY", "SELL"].includes(normalizedAction)) {
    return { error: "Select Buy or Sell before importing executions." };
  }
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const headerIndex = lines.findIndex((line) => {
    const headers = splitClipboardRow(line).map(normalizeHeader);
    return ["date", "price", "quantity", "amount"].every((name) =>
      headers.includes(name),
    );
  });
  if (headerIndex < 0) {
    return {
      error: "Paste Fidelity executions with Date, Price, Quantity, and Amount columns.",
    };
  }
  const headers = splitClipboardRow(lines[headerIndex]);
  const columns = {
    amount: findColumn(headers, "amount"),
    date: findColumn(headers, "date"),
    price: findColumn(headers, "price"),
    quantity: findColumn(headers, "quantity"),
  };
  const rows = [];
  let netTotal = null;
  const errors = [];
  for (const [offset, line] of lines.slice(headerIndex + 1).entries()) {
    const cells = splitClipboardRow(line);
    if (normalizeHeader(cells[0]) === "nettotal") {
      const values = cells
        .slice(1)
        .map(parseNumber)
        .filter((value) => Number.isFinite(value));
      if (values.length >= 2) {
        netTotal = {
          amount: Math.abs(values.at(-1)),
          quantity: Math.abs(values.at(-2)),
        };
      }
      break;
    }
    const rowNumber = headerIndex + offset + 2;
    const date = parseFidelityDate(cells[columns.date]);
    const price = parseNumber(cells[columns.price]);
    const quantity = parseNumber(cells[columns.quantity]);
    const amount = Math.abs(parseNumber(cells[columns.amount]));
    if (
      !date
      || !Number.isFinite(price)
      || price <= 0
      || !Number.isFinite(quantity)
      || quantity <= 0
      || !Number.isFinite(amount)
      || amount <= 0
    ) {
      errors.push(`row ${rowNumber}: invalid execution`);
      continue;
    }
    rows.push({ amount, date, price, quantity });
  }
  if (errors.length) {
    return { error: errors.slice(0, 3).join("; ") };
  }
  if (!rows.length) {
    return { error: "No valid Fidelity execution rows found." };
  }
  if (!netTotal) {
    return { error: "The Fidelity Net total row is required." };
  }
  const dates = new Set(rows.map((row) => row.date));
  if (dates.size !== 1) {
    return { error: "All executions must have the same trade date." };
  }
  const quantity = sum(rows.map((row) => row.quantity));
  if (!nearlyEqual(quantity, netTotal.quantity)) {
    return {
      error: `Execution rows total ${formatQuantity(quantity)} shares, but Net total shows ${formatQuantity(netTotal.quantity)}.`,
    };
  }
  const grossAmount = sum(rows.map((row) => row.price * row.quantity));
  const rawFees =
    normalizedAction === "SELL"
      ? grossAmount - netTotal.amount
      : netTotal.amount - grossAmount;
  if (rawFees < -0.05) {
    return {
      error: `Net total is inconsistent with a ${normalizedAction.toLowerCase()} trade.`,
    };
  }
  const fees = roundMoney(Math.max(0, rawFees));
  return {
    execution: {
      action: normalizedAction,
      execution_count: rows.length,
      execution_price: grossAmount / quantity,
      fees,
      gross_amount: grossAmount,
      net_amount: netTotal.amount,
      shares: quantity,
      trade_date: rows[0].date,
    },
  };
}

export function reconcileFidelityTradeLots({
  action,
  currentLots,
  executedShares,
  resultingLots,
}) {
  const normalizedAction = String(action || "").trim().toUpperCase();
  if (!["BUY", "SELL"].includes(normalizedAction)) {
    return { error: "Select Buy or Sell before reconciling lots." };
  }
  const shares = Number(executedShares);
  if (!Number.isFinite(shares) || shares <= 0) {
    return { error: "Execution quantity must be positive." };
  }
  const current = normalizeLots(currentLots, normalizedAction === "SELL");
  if (current.error) {
    return current;
  }
  const resulting = normalizeLots(resultingLots, false);
  if (resulting.error) {
    return resulting;
  }
  const currentGroups = groupLots(current.lots);
  const resultingGroups = groupLots(resulting.lots);
  const currentTotal = sum(current.lots.map((lot) => lot.shares));
  const resultingTotal = sum(resulting.lots.map((lot) => lot.shares));

  if (normalizedAction === "BUY") {
    for (const [key, group] of currentGroups) {
      const resultingShares = resultingGroups.get(key)?.shares || 0;
      if (resultingShares + SHARE_TOLERANCE < group.shares) {
        return {
          error: "The resulting holdings table reduces an existing tax lot for a buy.",
        };
      }
    }
    const addedShares = resultingTotal - currentTotal;
    if (!nearlyEqual(addedShares, shares)) {
      return {
        error: `Resulting holdings add ${formatQuantity(addedShares)} shares, but executions show ${formatQuantity(shares)}.`,
      };
    }
    return { added_shares: addedShares, sold_lots: [] };
  }

  if (!current.lots.length) {
    return { error: "The selected ticker has no recorded tax lots to sell." };
  }
  for (const [key, group] of resultingGroups) {
    const currentShares = currentGroups.get(key)?.shares || 0;
    if (group.shares > currentShares + SHARE_TOLERANCE) {
      return {
        error: "The resulting holdings table adds or changes a tax lot for a sell.",
      };
    }
  }
  const removedShares = currentTotal - resultingTotal;
  if (!nearlyEqual(removedShares, shares)) {
    return {
      error: `Resulting holdings remove ${formatQuantity(removedShares)} shares, but executions show ${formatQuantity(shares)}.`,
    };
  }
  const soldLots = [];
  for (const [key, group] of currentGroups) {
    let retainedShares = resultingGroups.get(key)?.shares || 0;
    for (const lot of group.lots) {
      const retained = Math.min(lot.shares, Math.max(0, retainedShares));
      const sold = lot.shares - retained;
      retainedShares -= retained;
      if (sold > SHARE_TOLERANCE) {
        soldLots.push({
          lot_id: lot.lot_id,
          shares: formatQuantity(sold),
        });
      }
    }
  }
  if (!nearlyEqual(sum(soldLots.map((lot) => Number(lot.shares))), shares)) {
    return { error: "Could not map the Fidelity sale to the recorded tax lots." };
  }
  return { removed_shares: removedShares, sold_lots: soldLots };
}

export function formatFidelityInputNumber(value, digits = 10) {
  return Number(value).toFixed(digits).replace(/\.?0+$/, "");
}

function normalizeLots(lots, requireLotId) {
  const normalized = [];
  for (const [index, lot] of (lots || []).entries()) {
    const shares = Number(lot.shares);
    const costBasis = Number(lot.cost_basis_per_share);
    const purchaseDate = String(lot.purchase_date || "").slice(0, 10);
    const lotId = String(lot.lot_id || "");
    if (
      !isValidDate(purchaseDate)
      || !Number.isFinite(shares)
      || shares <= 0
      || !Number.isFinite(costBasis)
      || costBasis < 0
      || (requireLotId && !lotId)
    ) {
      return { error: `Recorded tax lot ${index + 1} is incomplete.` };
    }
    normalized.push({
      cost_basis_per_share: costBasis,
      key: `${purchaseDate}|${Math.round(costBasis * 100)}`,
      lot_id: lotId,
      purchase_date: purchaseDate,
      shares,
    });
  }
  return { lots: normalized };
}

function groupLots(lots) {
  const groups = new Map();
  for (const lot of lots) {
    const group = groups.get(lot.key) || { lots: [], shares: 0 };
    group.lots.push(lot);
    group.shares += lot.shares;
    groups.set(lot.key, group);
  }
  return groups;
}

function splitClipboardRow(row) {
  const text = String(row || "").trim();
  if (!text) {
    return [];
  }
  const tabCells = text.split("\t").map((cell) => cell.trim());
  return tabCells.length > 1
    ? tabCells
    : text.split(/\s{2,}/).map((cell) => cell.trim());
}

function findColumn(headers, name) {
  return headers.findIndex((header) => normalizeHeader(header) === name);
}

function normalizeHeader(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function parseFidelityDate(value) {
  const text = String(value || "").trim();
  if (isValidDate(text)) {
    return text;
  }
  const match = text.match(/^([A-Za-z]{3})-(\d{1,2})-(\d{4})$/);
  const month = match ? FIDELITY_MONTHS[match[1].toLowerCase()] : "";
  if (!match || !month) {
    return "";
  }
  const date = `${match[3]}-${month}-${match[2].padStart(2, "0")}`;
  return isValidDate(date) ? date : "";
}

function isValidDate(value) {
  const text = String(value || "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return false;
  }
  const date = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === text;
}

function parseNumber(value) {
  const raw = String(value ?? "").trim();
  const negative = raw.startsWith("(") && raw.endsWith(")");
  const number = Number(
    raw
      .replace(/[$,%+]/g, "")
      .replace(/[()]/g, "")
      .replace(/,/g, "")
      .trim(),
  );
  return negative ? -number : number;
}

function nearlyEqual(left, right) {
  return Math.abs(left - right) <= SHARE_TOLERANCE;
}

function sum(values) {
  return values.reduce((total, value) => total + value, 0);
}

function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function formatQuantity(value) {
  return formatFidelityInputNumber(value, 8);
}
